import { useContext, useMemo, useState } from 'react';
import { Card, CardContent } from '@ui/card.tsx';
import { Button } from '@r/components/ui/button.tsx';
import { CsvFile } from '@r/components/import/csv-file.tsx';
import { FileUploader } from '@r/components/import/file-uploader.tsx';
import { ImportErrorPage } from '@r/components/import/import-error-page.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@r/components/ui/dialog.tsx';
import { useTranslation } from 'react-i18next';
import { useImportState } from '@r/hooks/use-import-state.ts';
import { useWorkspaceName } from '@r/hooks/use-workspace-name.ts';
import { parse, unparse } from 'papaparse';
import { toast } from 'sonner';
import { companiesBatchUpdate } from '@r/api/generated/client.ts';
import type {
  UpdateCompanyDtoCompanyUpdateJsonMergePatchModel,
  UpdateCompanyItemDtoCompanyUpdateModel
} from '@r/api/generated/client.schemas.ts';
import type { ImportError } from '@r/hooks/use-file-parser-with-errors.ts';
import { mapViolationsToErrors } from '@r/hooks/import-utils.ts';
import { FileWarningIcon } from 'lucide-react';
import { WorkspaceConfigContext } from '@r/context/workspace-config-context.ts';
import { isTypologyConversion } from '@r/utils/workspace-utils.ts';
import { getOrvalErrorMessage, isOrvalError } from '@r/api/orval-error-helper.ts';

type RawRow = Record<string, unknown>;
const COMMENT_MAX_LENGTH = 255;

const normalizeString = (value: unknown) =>
  value === undefined || value === null ? '' : String(value).trim();
const FIRST_DATA_LINE_NUMBER = 2;

// Lignes d'exemple du fichier CSV téléchargeable. Le séparateur "|" permet de
// rattacher plusieurs commerciaux à une même société.
const SAMPLE_ROWS = [
  {
    id: 1,
    uniqueCode: 'NESS0',
    city: 'Paris',
    responsiblesUniqueCodes: 'USERUNIQUECODE',
    comment: 'Un seul commercial'
  },
  {
    id: 2,
    uniqueCode: 'NESS1',
    city: 'Lyon',
    responsiblesUniqueCodes: 'USERUNIQUECODE|USERUNIQUECODE2',
    comment: 'Deux commerciaux'
  },
  {
    id: 3,
    uniqueCode: 'NESS2',
    city: 'Marseille',
    responsiblesUniqueCodes: 'USERUNIQUECODE|USERUNIQUECODE2|USERUNIQUECODE3',
    comment: 'Trois commerciaux'
  },
  {
    id: 4,
    uniqueCode: 'NESS3',
    city: 'Toulouse',
    responsiblesUniqueCodes: '',
    comment: 'Aucun commercial'
  },
  {
    id: 5,
    uniqueCode: 'NESS4',
    city: 'Nantes',
    responsiblesUniqueCodes: 'USERUNIQUECODE4|USERUNIQUECODE5',
    comment: 'Deux commerciaux sur une autre société'
  }
];

const buildFallbackErrors = (error: unknown, originalRows: RawRow[]): ImportError[] => {
  if (!isOrvalError(error)) {
    return [];
  }

  const message = getOrvalErrorMessage(error);

  // Essayer d’identifier un champ à partir du message
  let field: string | undefined;
  const lowerMsg = message.toLowerCase();
  if (lowerMsg.includes('unique_code')) {
    field = 'uniqueCode';
  } else if (lowerMsg.includes('responsible')) {
    field = 'responsiblesUniqueCodes';
  } else if (lowerMsg.includes('comment')) {
    field = 'comment';
  }

  // Associer la première ligne si disponible pour afficher dans la table
  const originalData = originalRows[0] as Record<string, unknown> | undefined;

  return [
    {
      message,
      error: message,
      field,
      line: FIRST_DATA_LINE_NUMBER,
      originalData
    }
  ];
};

export default function CompaniesModify() {
  const { t } = useTranslation();
  const { sanitizedWorkspaceName } = useWorkspaceName();
  const { state, setErrorState, resetState } = useImportState('companies');
  const workspaceConfigContext = useContext(WorkspaceConfigContext);
  const isTypology = isTypologyConversion(workspaceConfigContext);

  const csvHeaders = useMemo(() => {
    const base = [
      'id',
      'name',
      'uniqueCode',
      'siren',
      'tva',
      'addressLine1',
      'addressLine2',
      'addressLine3',
      'zipcode',
      'city',
      'countryAlpha3',
      'responsiblesUniqueCodes',
      'comment'
    ];

    return isTypology ? [...base, 'typologyName'] : [...base, 'pointCoeff'];
  }, [isTypology]);

  const [pendingPayload, setPendingPayload] =
    useState<UpdateCompanyDtoCompanyUpdateJsonMergePatchModel | null>(null);
  const [pendingRows, setPendingRows] = useState<RawRow[] | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [warningOpen, setWarningOpen] = useState(false);

  const sampleCsv = useMemo(() => {
    const rows = SAMPLE_ROWS.map(({ id, uniqueCode, city, responsiblesUniqueCodes, comment }) => {
      const base = [
        id,
        'nesso',
        uniqueCode,
        '123456789',
        'FR123456789',
        '1 rue de la paix',
        'Bâtiment B',
        'Étage 2',
        '75000',
        city,
        'FRA',
        responsiblesUniqueCodes,
        comment
      ];

      return isTypology ? [...base, 'Default'] : [...base, 1.5];
    });

    return unparse({
      fields: csvHeaders,
      data: rows
    });
  }, [csvHeaders, isTypology]);

  const filename = sanitizedWorkspaceName
    ? `${sanitizedWorkspaceName}_companies_modify_example.csv`
    : 'companies_modify_example.csv';

  const buildPayloadFromRows = (rows: RawRow[]) => {
    const errors: ImportError[] = [];
    const formattedRows: UpdateCompanyItemDtoCompanyUpdateModel[] = [];
    let hasEmptyCells = false;

    rows.forEach((row, index) => {
      const lineNumber = index + FIRST_DATA_LINE_NUMBER;
      const rawId = row.id ?? row.ID;
      const companyId = Number(rawId);

      if (!Number.isFinite(companyId)) {
        errors.push({
          message: t('file.parser.error.line', {
            line: lineNumber,
            field: 'id',
            error: t('import.error.id.invalid')
          }),
          line: lineNumber,
          field: 'id',
          originalData: row
        });
        return;
      }

      const pointCoeffRaw = row.pointCoeff ?? row.PointCoeff;
      let pointCoeff: number | null = null;

      if (!isTypology) {
        if (pointCoeffRaw === '' || pointCoeffRaw === undefined || pointCoeffRaw === null) {
          pointCoeff = null;
        } else {
          const parsedCoeff = Number(String(pointCoeffRaw).replace(',', '.'));
          if (Number.isNaN(parsedCoeff)) {
            errors.push({
              message: t('file.parser.error.line', {
                line: lineNumber,
                field: 'pointCoeff',
                error: t('import.error.number.invalid')
              }),
              line: lineNumber,
              field: 'pointCoeff',
              originalData: row
            });
          } else {
            pointCoeff = parsedCoeff;
          }
        }
      }

      const company = {
        name: normalizeString(row.name),
        uniqueCode: normalizeString(row.uniqueCode),
        siren: normalizeString(row.siren) || null,
        tva: normalizeString(row.tva) || null,
        addressLine1: normalizeString(row.addressLine1),
        addressLine2: normalizeString(row.addressLine2) || null,
        addressLine3: normalizeString(row.addressLine3) || null,
        zipcode: normalizeString(row.zipcode),
        city: normalizeString(row.city),
        countryAlpha3: normalizeString(row.countryAlpha3),
        responsiblesUniqueCodes: normalizeString(row.responsiblesUniqueCodes) || null,
        comment: normalizeString(row.comment) || null,
        ...(isTypology
          ? { typologyName: normalizeString(row.typologyName) || null }
          : { pointCoeff })
      };

      if ((company.comment?.length ?? 0) > COMMENT_MAX_LENGTH) {
        errors.push({
          message: t('file.parser.error.line', {
            line: lineNumber,
            field: 'comment',
            error: `Le commentaire ne doit pas depasser ${COMMENT_MAX_LENGTH} caracteres`
          }),
          line: lineNumber,
          field: 'comment',
          originalData: row
        });
      }

      const { comment: _comment, ...warningValues } = company;
      if (
        Object.values(warningValues).some(
          (value) => value === '' || value === null || value === undefined
        )
      ) {
        hasEmptyCells = true;
      }

      formattedRows.push({
        id: companyId,
        company
      });
    });

    return { formattedRows, errors, hasEmptyCells };
  };

  const mapViolations = (apiError: unknown, originalRows: RawRow[]): ImportError[] | undefined => {
    if (
      typeof apiError === 'object' &&
      apiError !== null &&
      'response' in apiError &&
      (apiError as { response?: { status?: number } }).response?.status === 422
    ) {
      const response = (apiError as { response?: { data?: { violations?: unknown[] } } }).response;
      const violations =
        response?.data &&
        (response.data as { violations?: Array<{ property: string; detail: string }> }).violations;

      if (violations && Array.isArray(violations)) {
        const violationLike = violations.map((violation) => ({
          ...violation,
          message: violation.detail
        }));
        return mapViolationsToErrors(violationLike, originalRows, t);
      }
    }
    return undefined;
  };

  const sendUpdate = async (
    payload: UpdateCompanyDtoCompanyUpdateJsonMergePatchModel,
    rawRows: RawRow[],
    originalFile: File | null
  ) => {
    const toastId = toast.loading(t('import.process.loading'));

    try {
      await companiesBatchUpdate(payload, { headers: { 'dry-run': 'true' } });
    } catch (error) {
      const mappedErrors = mapViolations(error, rawRows);
      toast.dismiss(toastId);
      if (mappedErrors && originalFile) {
        setErrorState(mappedErrors, originalFile);
        return;
      }
      if (isOrvalError(error) && originalFile) {
        const errors = buildFallbackErrors(error, rawRows);
        setErrorState(errors, originalFile);
        toast.error(errors[0]?.message ?? t('import.process.error'), { id: toastId });
        return;
      }
      toast.error(t('import.process.error'), { id: toastId });
      return;
    }

    try {
      await companiesBatchUpdate(payload);
      toast.success(t('import.process.success'), { id: toastId });
      resetState();
      setPendingPayload(null);
      setPendingRows(null);
      setPendingFile(null);
    } catch (error) {
      const mappedErrors = mapViolations(error, rawRows);
      toast.dismiss(toastId);
      if (mappedErrors && originalFile) {
        setErrorState(mappedErrors, originalFile);
        return;
      }
      if (isOrvalError(error) && originalFile) {
        const errors = buildFallbackErrors(error, rawRows);
        setErrorState(errors, originalFile);
        toast.error(errors[0]?.message ?? t('import.process.error'), { id: toastId });
        return;
      }
      toast.error(t('import.process.error'), { id: toastId });
    }
  };

  const handleParse = async (files: File[], skipWarning?: boolean) => {
    const file = files[0];
    const toastId = toast.loading(t('import.process.analyzing'));

    return new Promise<void>((resolve) => {
      parse<RawRow>(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (result) => {
          toast.dismiss(toastId);

          if (result.errors.length > 0) {
            toast.error(t('import.process.parsing.error'));
            return resolve();
          }

          const { formattedRows, errors, hasEmptyCells } = buildPayloadFromRows(result.data);

          if (errors.length > 0) {
            setPendingFile(file);
            setErrorState(errors, file);
            return resolve();
          }

          const payload: UpdateCompanyDtoCompanyUpdateJsonMergePatchModel = { data: formattedRows };
          setPendingPayload(payload);
          setPendingRows(result.data);
          setPendingFile(file);

          if (hasEmptyCells && !skipWarning) {
            setWarningOpen(true);
            return resolve();
          }

          await sendUpdate(payload, result.data, file);
          resolve();
        }
      });
    });
  };

  const handleRetry = async () => {
    // Si on a des lignes en erreur, on tente d'exclure les lignes concernées (force import)
    if (pendingRows && pendingRows.length > 0 && state.errors.length > 0 && state.originalFile) {
      const errorLines = new Set(
        state.errors
          .map((e) => (typeof e.line === 'number' ? e.line - FIRST_DATA_LINE_NUMBER : null))
          .filter((v): v is number => v !== null)
      );
      const filteredRows = pendingRows.filter((_, idx) => !errorLines.has(idx));

      if (filteredRows.length === 0) {
        // Plus aucune ligne exploitable
        setErrorState(state.errors, state.originalFile);
        return;
      }

      const { formattedRows, errors } = buildPayloadFromRows(filteredRows);
      if (errors.length > 0) {
        setErrorState(errors, state.originalFile);
        return;
      }

      await sendUpdate({ data: formattedRows }, filteredRows, state.originalFile);
      return;
    }

    // Fallback: relancer un parse forcé
    if (state.originalFile) {
      await handleParse([state.originalFile], true);
    }
  };

  const handleConfirmWarning = async () => {
    setWarningOpen(false);
    if (pendingPayload && pendingRows) {
      await sendUpdate(pendingPayload, pendingRows, pendingFile);
    }
  };

  if (state.isError && state.errors.length > 0 && state.originalFile) {
    return (
      <ImportErrorPage
        errors={state.errors}
        originalFile={state.originalFile}
        onRetry={handleRetry}
        onNewFile={resetState}
        importType="companies"
      />
    );
  }

  return (
    <div className="space-y-10">
      <h1 className="text-4xl font-bold">{t('admin.companies.modify.title')}</h1>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <h2 className="text-xl font-bold">{t('admin.companies.modify.csv.title')}</h2>
          <p className="text-muted-foreground">{t('admin.companies.modify.csv.description')}</p>
          <p className="text-muted-foreground">{t('admin.companies.modify.csv.description.2')}</p>
          <CsvFile csv={sampleCsv} filename={filename} label={t('common.csv.example.button')} />
          <FileUploader
            accept={{
              'text/csv': [],
              'application/vnd.ms-excel': [],
              'application/csv': [],
              'text/x-csv': [],
              'application/x-csv': [],
              'text/comma-separated-values': [],
              'text/x-comma-separated-values': []
            }}
            onParse={(uploadedFiles) => handleParse(uploadedFiles)}
          />
        </CardContent>
      </Card>

      <Dialog open={warningOpen} onOpenChange={setWarningOpen}>
        <DialogContent className="p-14 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex gap-2 items-center text-2xl">
              {t('admin.companies.modify.warning.title')}
              <FileWarningIcon className="text-red-500" />
            </DialogTitle>
            <DialogDescription className="font-bold text-foreground">
              {t('admin.companies.modify.warning.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 justify-end">
            <Button rounded="full" variant="outline" onClick={handleConfirmWarning}>
              {t('common.continue')}
            </Button>
            <Button rounded="full" onClick={() => setWarningOpen(false)}>
              {t('common.cancel')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
